import { Alert } from '@designsforhealth/dfh-react-components'
import { Grid } from '@material-ui/core'
import { Subscribe } from '@react-rxjs/core'
import classnames from 'classnames'
import React, { useReducer } from 'react'
import styled from 'styled-components'

import { CartFragment } from '../../../graphql/magento'
import { useResizeObserver } from '../../../hooks/useResizeObserver'
import CartLayout from '../../../layouts/buy/CartLayout'
import { setCartFooterHeightFromBoxSize } from '../../../lib/cart/footer'
import { reduceCartState } from '../../../lib/cart/reducers/cart-state'
import { CartFooterSpacer } from '../../cart/CartFooterSpacer'
import { CartItemErrors } from '../../cart/CartItemErrors'
import CaliforniaProp65Warning from '../summary/CaliforniaProp65Warning'
import { CartContinueButtons } from './CartContinueButtons'
import CartFooter from './CartFooter'
import CartHeading from './CartHeading'
import { CartItemList } from './CartItemList'

const StyledCartFooterSpacer = styled(CartFooterSpacer)`
  @media (min-width: 600px) {
    display: none;
  }
`

const StyledGrid = styled(Grid)`
  border-left: 1px solid ${({ theme }) => theme.colors.coolGray.cool250};
  border-right: 1px solid ${({ theme }) => theme.colors.coolGray.cool250};
`

const StyledCartItemErrors = styled(CartItemErrors)`
  ${Alert} {
    margin-top: 10px;
    margin-left: 10px;
    margin-right: 10px;
  }
`

export interface CartProps {
  cart: CartFragment
  className?: string
}

const Cart: React.FC<CartProps> = ({ cart, className }) => {
  const [{ codesCollapsed, formDisabled, summaryVisible }, dispatch] = useReducer(reduceCartState, {
    codesCollapsed: true,
    formDisabled: false,
    requestsPending: [],
    summaryVisible: false,
  })
  const resizeObserverRef = useResizeObserver(setCartFooterHeightFromBoxSize)

  const { shipping_addresses: shippingAddresses } = cart
  const [shippingAddress] = shippingAddresses || []

  return (
    <CartLayout cart={cart}>
      <Grid
        alignItems="center"
        justifyContent="center"
        className={className}
        container
        item
        xs={12}
      >
        <CartContinueButtons
          formDisabled={formDisabled}
          className="continueButtonsAboveFold"
          id="continue-buttons"
        />
        <CartHeading cart={cart} />
        <Subscribe>
          <StyledGrid justifyContent="center" container item xs={12}>
            <StyledCartItemErrors />
          </StyledGrid>
        </Subscribe>
        <CartItemList cart={cart} dispatch={dispatch} />
        <CaliforniaProp65Warning
          className={classnames('mobileProp65Warning', {
            summaryVisible,
          })}
          items={cart.items || []}
          shippingAddress={shippingAddress}
        />
        <StyledCartFooterSpacer
          // allow for help text and applied promo toggle in fallback
          fallbackHeight={345}
        />
        <CartFooter
          cart={cart}
          codesCollapsed={codesCollapsed}
          dispatch={dispatch}
          formDisabled={formDisabled}
          ref={resizeObserverRef}
          summaryVisible={summaryVisible}
        >
          <CartContinueButtons
            formDisabled={formDisabled}
            className="continueButtonsFooter"
            id="continue-buttons-footer"
          />
        </CartFooter>
      </Grid>
    </CartLayout>
  )
}

const StyledCart = styled(Cart)`
  .mobileProp65Warning {
    margin-top: 0;
    padding: 20px 15px 8px;
    & a {
      color: #000000;
    }
  }
  .continueButtonsAboveFold {
    margin-top: 10px;
    margin-bottom: 20px;
    display: none;
  }

  @media (min-width: 600px) {
    .mobileProp65Warning {
      border: 1px solid ${({ theme }) => theme.colors.coolGray.cool250};
      border-top: none;
    }
  }

  @media (min-width: 600px) {
    .continueButtonsAboveFold {
      display: flex;
    }
  }
`

export default StyledCart
